import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Card } from './ui/card';
import {
  Type, Palette, Music, Scissors, Play, Pause, RotateCcw,
  Download, X, Plus, Move, Trash2, Volume2, VolumeX, Sparkles
} from 'lucide-react';

const FILTERS = [
  { id: 'none', label: 'Normal', css: 'none' },
  { id: 'grayscale', label: 'B&W', css: 'grayscale(100%)' },
  { id: 'sepia', label: 'Sepia', css: 'sepia(80%)' },
  { id: 'warm', label: 'Warm', css: 'saturate(1.4) hue-rotate(-10deg)' },
  { id: 'cool', label: 'Cool', css: 'saturate(0.9) hue-rotate(20deg) brightness(1.05)' },
  { id: 'vintage', label: 'Vintage', css: 'sepia(40%) contrast(1.1) brightness(0.95)' },
  { id: 'vivid', label: 'Vivid', css: 'saturate(1.8) contrast(1.1)' },
  { id: 'dramatic', label: 'Drama', css: 'contrast(1.4) brightness(0.9) saturate(0.8)' },
  { id: 'fade', label: 'Fade', css: 'brightness(1.1) saturate(0.7) contrast(0.9)' },
];

const FONTS = ['Arial', 'Georgia', 'Courier New', 'Impact', 'Comic Sans MS', 'Verdana'];
const TEXT_COLORS = ['#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff6600', '#ff00ff'];

const VideoEditor = ({ videoUrl, onSave, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const timelineRef = useRef(null);
  const audioRef = useRef(null);
  const animFrameRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeTab, setActiveTab] = useState('trim');

  // Trim state
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(100);

  // Filter state
  const [selectedFilter, setSelectedFilter] = useState('none');
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);

  // Text overlay state
  const [textOverlays, setTextOverlays] = useState([]);
  const [editingText, setEditingText] = useState(null);
  const [dragging, setDragging] = useState(null);

  // Music state
  const [musicUrl, setMusicUrl] = useState(null);
  const [musicVolume, setMusicVolume] = useState(50);
  const [videoVolume, setVideoVolume] = useState(100);

  // Export state
  const [exporting, setExporting] = useState(false);

  const getFilterCSS = useCallback(() => {
    const filterObj = FILTERS.find(f => f.id === selectedFilter);
    let css = filterObj?.css || 'none';
    const extras = [];
    if (brightness !== 100) extras.push(`brightness(${brightness / 100})`);
    if (contrast !== 100) extras.push(`contrast(${contrast / 100})`);
    if (css === 'none' && extras.length) return extras.join(' ');
    if (extras.length) return `${css} ${extras.join(' ')}`;
    return css;
  }, [selectedFilter, brightness, contrast]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onLoaded = () => {
      setDuration(video.duration);
      setTrimEnd(100);
    };
    video.addEventListener('loadedmetadata', onLoaded);
    return () => video.removeEventListener('loadedmetadata', onLoaded);
  }, [videoUrl]);

  useEffect(() => {
    if (!isPlaying) { cancelAnimationFrame(animFrameRef.current); return; }
    const tick = () => {
      const video = videoRef.current;
      if (!video) return;
      const endTime = (trimEnd / 100) * duration;
      if (video.currentTime >= endTime) {
        video.pause();
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
        return;
      }
      setCurrentTime(video.currentTime);
      renderCanvas();
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPlaying, trimEnd, duration]);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    ctx.filter = getFilterCSS();
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.filter = 'none';
    textOverlays.forEach(overlay => {
      ctx.font = `${overlay.bold ? 'bold ' : ''}${overlay.size}px ${overlay.font}`;
      if (overlay.stroke) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 3;
        ctx.strokeText(overlay.text, overlay.x * canvas.width / 100, overlay.y * canvas.height / 100);
      }
      ctx.fillStyle = overlay.color;
      ctx.fillText(overlay.text, overlay.x * canvas.width / 100, overlay.y * canvas.height / 100);
    });
  }, [getFilterCSS, textOverlays]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const startTime = (trimStart / 100) * duration;
      if (video.currentTime < startTime || video.currentTime >= (trimEnd / 100) * duration) {
        video.currentTime = startTime;
        if (audioRef.current) audioRef.current.currentTime = 0;
      }
      video.play();
      if (audioRef.current) audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const seekTo = (pct) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = (pct / 100) * duration;
    setCurrentTime(video.currentTime);
    renderCanvas();
  };

  const addTextOverlay = () => {
    const newOverlay = {
      id: Date.now(),
      text: 'Your Text',
      x: 50, y: 50,
      size: 32,
      color: '#ffffff',
      font: 'Arial',
      bold: true,
      stroke: true
    };
    setTextOverlays([...textOverlays, newOverlay]);
    setEditingText(newOverlay.id);
  };

  const updateOverlay = (id, updates) => {
    setTextOverlays(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o));
  };

  const removeOverlay = (id) => {
    setTextOverlays(prev => prev.filter(o => o.id !== id));
    if (editingText === id) setEditingText(null);
  };

  const handleCanvasMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const hit = textOverlays.find(o => {
      return Math.abs(o.x - x) < 15 && Math.abs(o.y - y) < 10;
    });
    if (hit) {
      setDragging(hit.id);
      setEditingText(hit.id);
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!dragging) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(5, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(5, Math.min(95, ((e.clientY - rect.top) / rect.height) * 100));
    updateOverlay(dragging, { x, y });
    renderCanvas();
  };

  const handleCanvasMouseUp = () => setDragging(null);

  const handleMusicUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setMusicUrl(url);
  };

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = musicVolume / 100;
  }, [musicVolume]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.volume = videoVolume / 100;
  }, [videoVolume]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const startTime = (trimStart / 100) * duration;
      const endTime = (trimEnd / 100) * duration;

      video.currentTime = startTime;
      await new Promise(r => setTimeout(r, 200));

      const stream = canvas.captureStream(30);
      if (musicUrl && audioRef.current) {
        try {
          const audioCtx = new AudioContext();
          const dest = audioCtx.createMediaStreamDestination();
          const videoSrc = audioCtx.createMediaElementSource(video);
          videoSrc.connect(dest);
          videoSrc.connect(audioCtx.destination);
          if (audioRef.current) {
            const musicSrc = audioCtx.createMediaElementSource(audioRef.current);
            musicSrc.connect(dest);
            musicSrc.connect(audioCtx.destination);
          }
          dest.stream.getAudioTracks().forEach(t => stream.addTrack(t));
        } catch (err) {
          console.warn('Audio mixing failed, exporting video only:', err);
        }
      }

      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };

      const exportDone = new Promise((resolve) => {
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };
      });

      recorder.start();
      video.play();
      if (audioRef.current) audioRef.current.play().catch(() => {});
      setIsPlaying(true);

      const renderExport = () => {
        if (video.currentTime >= endTime || video.paused) {
          video.pause();
          if (audioRef.current) audioRef.current.pause();
          recorder.stop();
          setIsPlaying(false);
          return;
        }
        renderCanvas();
        requestAnimationFrame(renderExport);
      };
      requestAnimationFrame(renderExport);

      const blob = await exportDone;
      onSave(blob);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const tabs = [
    { id: 'trim', icon: Scissors, label: 'Trim' },
    { id: 'filters', icon: Sparkles, label: 'Filters' },
    { id: 'text', icon: Type, label: 'Text' },
    { id: 'music', icon: Music, label: 'Music' },
  ];

  const editingOverlay = textOverlays.find(o => o.id === editingText);

  return (
    <div className="flex flex-col h-full max-h-[85vh]" data-testid="video-editor">
      {/* Hidden video element */}
      <video ref={videoRef} src={videoUrl} className="hidden" crossOrigin="anonymous" preload="auto" />
      {musicUrl && <audio ref={audioRef} src={musicUrl} className="hidden" />}

      {/* Canvas Preview */}
      <div className="relative bg-black rounded-lg overflow-hidden flex-shrink-0 mb-3">
        <canvas
          ref={canvasRef}
          className="w-full max-h-[40vh] object-contain cursor-move"
          style={{ filter: isPlaying ? 'none' : getFilterCSS() }}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseUp}
        />
        {!isPlaying && textOverlays.map(overlay => (
          <div
            key={overlay.id}
            className={`absolute pointer-events-none ${editingText === overlay.id ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
            style={{
              left: `${overlay.x}%`,
              top: `${overlay.y}%`,
              transform: 'translate(-50%, -50%)',
              font: `${overlay.bold ? 'bold ' : ''}${overlay.size}px ${overlay.font}`,
              color: overlay.color,
              textShadow: overlay.stroke ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none',
              whiteSpace: 'nowrap'
            }}
          >
            {overlay.text}
          </div>
        ))}
      </div>

      {/* Timeline / Playback */}
      <div className="flex items-center gap-3 mb-3 px-1">
        <Button variant="ghost" size="sm" onClick={togglePlayPause} data-testid="play-pause-btn">
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </Button>
        <span className="text-xs text-gray-500 w-10">{formatTime(currentTime)}</span>
        <div
          ref={timelineRef}
          className="flex-1 relative h-8 bg-gray-200 rounded cursor-pointer"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seekTo(((e.clientX - rect.left) / rect.width) * 100);
          }}
        >
          {/* Trim range */}
          <div
            className="absolute top-0 h-full bg-blue-100 rounded"
            style={{ left: `${trimStart}%`, width: `${trimEnd - trimStart}%` }}
          />
          {/* Playhead */}
          <div
            className="absolute top-0 w-0.5 h-full bg-red-500 z-10"
            style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 w-10">{formatTime(duration)}</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto min-h-0 pb-3">
        {activeTab === 'trim' && (
          <div className="space-y-4 px-1">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Start Point ({formatTime((trimStart / 100) * duration)})</label>
              <Slider value={[trimStart]} min={0} max={trimEnd - 1} step={1} onValueChange={([v]) => setTrimStart(v)} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">End Point ({formatTime((trimEnd / 100) * duration)})</label>
              <Slider value={[trimEnd]} min={trimStart + 1} max={100} step={1} onValueChange={([v]) => setTrimEnd(v)} />
            </div>
            <p className="text-sm text-gray-500">
              Duration: {formatTime(((trimEnd - trimStart) / 100) * duration)}
            </p>
          </div>
        )}

        {activeTab === 'filters' && (
          <div className="space-y-4 px-1">
            <div className="grid grid-cols-3 gap-2">
              {FILTERS.map(filter => (
                <button
                  key={filter.id}
                  onClick={() => { setSelectedFilter(filter.id); renderCanvas(); }}
                  className={`p-3 rounded-lg border text-sm font-medium transition-colors ${
                    selectedFilter === filter.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`filter-${filter.id}`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Brightness: {brightness}%</label>
              <Slider value={[brightness]} min={50} max={150} step={1} onValueChange={([v]) => { setBrightness(v); renderCanvas(); }} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Contrast: {contrast}%</label>
              <Slider value={[contrast]} min={50} max={150} step={1} onValueChange={([v]) => { setContrast(v); renderCanvas(); }} />
            </div>
          </div>
        )}

        {activeTab === 'text' && (
          <div className="space-y-3 px-1">
            <Button onClick={addTextOverlay} size="sm" className="w-full" data-testid="add-text-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Text Overlay
            </Button>
            {textOverlays.map(overlay => (
              <Card key={overlay.id} className={`p-3 space-y-2 ${editingText === overlay.id ? 'ring-2 ring-blue-500' : ''}`}>
                <div className="flex items-center justify-between">
                  <button onClick={() => setEditingText(overlay.id)} className="text-sm font-medium text-blue-600 flex items-center gap-1">
                    <Move className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => removeOverlay(overlay.id)} className="text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <Input
                  value={overlay.text}
                  onChange={(e) => { updateOverlay(overlay.id, { text: e.target.value }); renderCanvas(); }}
                  placeholder="Enter text..."
                />
                <div className="flex gap-2 flex-wrap">
                  {TEXT_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => { updateOverlay(overlay.id, { color }); renderCanvas(); }}
                      className={`w-6 h-6 rounded-full border-2 ${overlay.color === color ? 'border-blue-500 scale-110' : 'border-gray-200'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <select
                    value={overlay.font}
                    onChange={(e) => { updateOverlay(overlay.id, { font: e.target.value }); renderCanvas(); }}
                    className="text-sm border rounded px-2 py-1 flex-1"
                  >
                    {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <Input
                    type="number"
                    value={overlay.size}
                    onChange={(e) => { updateOverlay(overlay.id, { size: parseInt(e.target.value) || 24 }); renderCanvas(); }}
                    className="w-16"
                    min={12} max={120}
                  />
                </div>
              </Card>
            ))}
            {textOverlays.length > 0 && (
              <p className="text-xs text-gray-400 text-center">Drag text on the canvas to reposition</p>
            )}
          </div>
        )}

        {activeTab === 'music' && (
          <div className="space-y-4 px-1">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">Add Background Music</label>
              <Input type="file" accept="audio/*" onChange={handleMusicUpload} data-testid="music-upload-input" />
              {musicUrl && (
                <div className="mt-2 flex items-center gap-2">
                  <Music className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600 font-medium">Music loaded</span>
                  <Button variant="ghost" size="sm" onClick={() => setMusicUrl(null)}>
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                <Volume2 className="h-4 w-4" /> Video Volume: {videoVolume}%
              </label>
              <Slider value={[videoVolume]} min={0} max={100} step={1} onValueChange={([v]) => setVideoVolume(v)} />
            </div>
            {musicUrl && (
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                  <Music className="h-4 w-4" /> Music Volume: {musicVolume}%
                </label>
                <Slider value={[musicVolume]} min={0} max={100} step={1} onValueChange={([v]) => setMusicVolume(v)} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-3 border-t">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          disabled={exporting}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
          data-testid="export-video-btn"
        >
          {exporting ? (
            <span className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Exporting...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Apply & Save
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};

export default VideoEditor;
